import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiEdit, FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import "./AllUsersList.css";

const AllUsersList = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // --------------------------------------------------
  // GET ALL USERS
  // --------------------------------------------------
  const getAllUsers = async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${API_BASE_URL}/api/admin/getAccountUsers`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
        }
      );

      if (
        response.data &&
        response.data.status === "1" &&
        Array.isArray(response.data.data)
      ) {
        setUsers(response.data.data);
        setFilteredUsers(response.data.data);
      } else {
        setUsers([]);
        setFilteredUsers([]);
      }
    } catch (err) {
      console.error("Error fetching users:", err);

      setError(
        err.response?.data?.message ||
          "Unable to fetch users. Please try again."
      );

      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // API CALL ON PAGE LOAD
  // --------------------------------------------------
  useEffect(() => {
    getAllUsers();
  }, []);

  // --------------------------------------------------
  // SEARCH
  // --------------------------------------------------
  useEffect(() => {
    const searchText = search.trim().toLowerCase();

    if (!searchText) {
      setFilteredUsers(users);
      setCurrentPage(1);
      return;
    }

    const filtered = users.filter((user) => {
      const name = user.name?.toLowerCase() || "";
      const username = user.username?.toLowerCase() || "";

      return (
        name.includes(searchText) ||
        username.includes(searchText)
      );
    });

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [search, users]);

  // --------------------------------------------------
  // PAGINATION
  // --------------------------------------------------
  const totalPages = Math.ceil(
    filteredUsers.length / entriesPerPage
  );

  const indexOfLastUser = currentPage * entriesPerPage;
  const indexOfFirstUser = indexOfLastUser - entriesPerPage;

  const currentUsers = filteredUsers.slice(
    indexOfFirstUser,
    indexOfLastUser
  );

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  // --------------------------------------------------
  // EDIT USER
  // --------------------------------------------------
  const handleEdit = (user) => {
    navigate(`/edit-user/${user.id}`, {
      state: {
        user: user,
      },
    });
  };

  return (
    <div className="users-page">
      <div className="users-container">

        {/* -------------------------------------------- */}
        {/* HEADER */}
        {/* -------------------------------------------- */}

        <div className="users-header">

          <div>
            <h2>User List</h2>
            <p>Manage account users</p>
          </div>

          <div className="users-search">
            <FiSearch size={18} />

            <input
              type="text"
              placeholder="Search name or username..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

        </div>

        {/* -------------------------------------------- */}
        {/* ERROR */}
        {/* -------------------------------------------- */}

        {error && (
          <div className="users-error">
            {error}
          </div>
        )}

        {/* -------------------------------------------- */}
        {/* TABLE */}
        {/* -------------------------------------------- */}

        <div className="users-table-wrapper">

          {loading ? (
            <div className="users-loading">
              Loading users...
            </div>
          ) : (
            <table className="users-table">

              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th className="action-column">Action</th>
                </tr>
              </thead>

              <tbody>

                {currentUsers.length > 0 ? (
                  currentUsers.map((user) => (
                    <tr key={user.id}>

                      <td>
                        {user.name || "-"}
                      </td>

                      <td>
                        {user.username || "-"}
                      </td>

                      <td className="action-column">

                        <button
                          type="button"
                          className="edit-user-btn"
                          onClick={() => handleEdit(user)}
                          title="Edit User"
                        >
                          <FiEdit size={16} />
                          <span>Edit</span>
                        </button>

                      </td>

                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="3"
                      className="no-users"
                    >
                      {search
                        ? "No users found."
                        : "No users available."}
                    </td>
                  </tr>
                )}

              </tbody>

            </table>
          )}

        </div>

        {/* -------------------------------------------- */}
        {/* FOOTER / PAGINATION */}
        {/* -------------------------------------------- */}

        {!loading && filteredUsers.length > 0 && (
          <div className="users-footer">

            <div className="users-count">
              Showing{" "}
              <strong>
                {indexOfFirstUser + 1}
              </strong>{" "}
              to{" "}
              <strong>
                {Math.min(
                  indexOfLastUser,
                  filteredUsers.length
                )}
              </strong>{" "}
              of{" "}
              <strong>
                {filteredUsers.length}
              </strong>{" "}
              users
            </div>

            <div className="users-pagination">

              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <span>
                Page {currentPage} of{" "}
                {totalPages || 1}
              </span>

              <button
                type="button"
                onClick={handleNext}
                disabled={
                  currentPage === totalPages ||
                  totalPages === 0
                }
              >
                Next
              </button>

            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default AllUsersList;